import { useState, type ReactNode } from "react";
import {
  AppButton,
  EmptyState,
  PageHeader,
} from "../../components/ui";
import { Box, Stack, Tab, Tabs, Typography } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useSelector } from "react-redux";
import { ApplicationRole, UserViewModel } from "../../api";
import UserProfileForm from "../Admin/Users/UserDetails/UserProfileForm";
import ChangePasswordDialog from "./ChangePasswordDialog";

type SettingsTab = "personal" | "security";

function isAttorneyUser(user: UserViewModel) {
  return (
    user.roles?.includes(ApplicationRole.ATTORNEY) ||
    user.roles?.includes(ApplicationRole.USER) ||
    false
  );
}

function TabPanel({
  value,
  tab,
  children,
}: {
  value: SettingsTab;
  tab: SettingsTab;
  children: ReactNode;
}) {
  if (value !== tab) return null;
  return <Box sx={{ pt: 2.5 }}>{children}</Box>;
}

export default function ProfileSettings() {
  const [tab, setTab] = useState<SettingsTab>("personal");
  const [isEdit, setIsEdit] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const user = useSelector(
    (state: { user?: { user?: UserViewModel } }) => state.user?.user
  );

  const pageSx = {
    width: "100%",
    maxWidth: 920,
    mx: "auto",
    pb: 4,
  } as const;

  if (!user) {
    return (
      <Box sx={pageSx}>
        <PageHeader title="Profile settings" />
        <EmptyState
          title="Profile unavailable"
          description="Sign in again to view and update your account information."
        />
      </Box>
    );
  }

  const attorney = isAttorneyUser(user);

  return (
    <Box sx={pageSx}>
      <PageHeader
        title="Profile settings"
        actions={
          tab === "personal" && !isEdit ? (
            <AppButton
              variant="outlined"
              color="inherit"
              startIcon={<EditOutlinedIcon />}
              onClick={() => setIsEdit(true)}
            >
              Edit
            </AppButton>
          ) : undefined
        }
      />

      <Box
        sx={{
          bgcolor: "#FFFFFF",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "12px",
          boxShadow: "0 1px 2px rgba(27, 30, 51, 0.04)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: { xs: 1.5, md: 2 },
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, next: SettingsTab) => {
              setTab(next);
              setIsEdit(false);
            }}
          >
            <Tab value="personal" label="Personal" />
            <Tab value="security" label="Security" />
          </Tabs>
        </Box>

        <Box sx={{ px: { xs: 2, md: 2.75 }, pb: { xs: 2, md: 2.75 } }}>
          <TabPanel value={tab} tab="personal">
            <UserProfileForm
              user={user}
              isEdit={isEdit}
              isAttorney={attorney}
              updateStored
              allowRoleEdit={false}
              onCancelEdit={() => setIsEdit(false)}
              onSave={() => setIsEdit(false)}
            />
          </TabPanel>

          <TabPanel value={tab} tab="security">
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(27, 30, 51, 0.06)",
                    color: "text.primary",
                    flexShrink: 0,
                  }}
                >
                  <LockOutlinedIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Password
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Update the password you use to sign in.
                  </Typography>
                </Box>
              </Stack>
              <AppButton
                variant="contained"
                color="primary"
                onClick={() => setChangePasswordOpen(true)}
                sx={{ flexShrink: 0 }}
              >
                Change password
              </AppButton>
            </Stack>
          </TabPanel>
        </Box>
      </Box>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </Box>
  );
}
